import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Badge, Button, Copy01, DotsGrid, DotsVertical, Edit01, Menu, Plus, Trash01 } from "@opendate-app/setlist";
import { AddOnsTableFrame } from "../../ConfirmTicketing/components/AddOnsTable";
import { draggablePropsWithStyle } from "../../ConfirmTicketing/components/AddOnRow";
import AddOnDrawer from "./AddOnDrawer";
import SeatingChartPicker from "./SeatingChartPicker";
import AddOnsCopySection from "./AddOnsCopySection";
import { ADD_ON_CHART, DAY_OF_SHOW_NAME, blankAddOn, copyName, moveRow } from "../scheduleBuilder";
import { offsetParts } from "./ShowDetailOffset";

const money = (value) => (Number(value) ? `$${Number(value).toFixed(2)}` : "FREE");

const priceLabel = (addOn) => (addOn.sectionKey && !Number(addOn.price) ? "Set a price" : money(addOn.price));

const atLabel = (name, minutes) => {
  if (!Number(minutes)) return name;
  const { amount, unit, direction } = offsetParts(minutes);
  return `${amount} ${amount === "1" ? unit.replace(/s$/, "") : unit} ${direction} ${name}`;
};

const onSaleLabel = (addOn) =>
  addOn.startActivity
    ? `${atLabel(addOn.startActivity, addOn.startOffsetMinutes)} → ${atLabel(addOn.endActivity, addOn.endOffsetMinutes)}`
    : "Each date's public on-sale";

const pricePointLabel = (point) =>
  point.showActivityName ? atLabel(point.showActivityName, point.offsetMinutes) : `${DAY_OF_SHOW_NAME} (doors)`;

// The confirm's AddOnRow, minus its sold counts and server actions: a series
// add-on has no sales until its dates exist.
const SeriesAddOnRow = ({ addOn, provided, snapshot, actions }) => {
  const seated = Boolean(addOn.sectionKey);
  const pricePoints = Number(addOn.price) ? addOn.pricePoints || [] : [];
  return (
    <tr ref={provided.innerRef} {...draggablePropsWithStyle(provided, snapshot)} data-testid="series-add-on-row">
      <td data-label="" className="px-1 text-center d-none d-md-table-cell" style={{ verticalAlign: "middle" }}>
        <span
          {...provided.dragHandleProps}
          aria-label="Reorder add-on"
          style={{ opacity: seated ? 0 : 1, color: "var(--sl-text-disabled)" }}
          className="py-0 btn btn-link btn-sm small"
        >
          <DotsGrid width={16} height={16} aria-hidden="true" />
        </span>
      </td>
      <td data-label="Ticket Name" className="overflow-ellipsis">
        <div>
          <strong>{addOn.name}</strong>
          {addOn.dynamicPricingEnabled && (
            <span className="ml-2">
              <Badge color="blue" size="sm">⚡ Dynamic</Badge>
            </span>
          )}
        </div>
        {addOn.description && <div className="series-edit-hint">{addOn.description}</div>}
        {pricePoints.map((point, index) => (
          <div key={point.id} style={{ marginTop: "12px", fontSize: "12px" }}>
            <span style={{ marginRight: "4px" }}>
              <Badge color="brand" size="sm">Price Change {index + 1}</Badge>
            </span>
            {point.name}
          </div>
        ))}
      </td>
      <td data-label="On Sale Date">
        <div>{onSaleLabel(addOn)}</div>
        {pricePoints.map((point) => (
          <div key={point.id} style={{ marginTop: "12px", fontSize: "12px" }}>{pricePointLabel(point)}</div>
        ))}
      </td>
      <td data-label="Price">
        <div>{priceLabel(addOn)}</div>
        {pricePoints.map((point) => (
          <div key={point.id} style={{ marginTop: "12px", fontSize: "12px" }}>{money(point.price)}</div>
        ))}
      </td>
      <td data-label="Inventory">{addOn.inventory}</td>
      <td data-label="" className="text-center" style={{ verticalAlign: "middle" }}>
        <Menu
          placement="bottom-end"
          items={actions}
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${addOn.name}`} />}
        />
      </td>
    </tr>
  );
};

const AddOnsTab = ({
  addOns,
  ticketTypeNames,
  showActivityNames = [],
  manualTaxes = [],
  canUseDynamicPricing = false,
  dynamicPricingDefault = false,
  settings,
  addOnsCopyDefaults,
  messageTemplates,
  messageTemplatesUrl,
  onSettingsChange,
  onChange,
  addOnCharts = [],
  addOnSeatingChartId,
  sectionsUrl,
  editChartUrl,
  showTypeLabel,
  onPickChart,
  onRemoveChart,
  onChooseVenue,
  timedEntry = false,
  defaultCheckInPeriod = "all_time",
  feeQuoteUrl,
  venueOwnershipId,
  csrfToken,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [copying, setCopying] = useState(null);
  const [choosingChart, setChoosingChart] = useState(false);
  const chartShown = addOnCharts.length > 0 && (Boolean(addOnSeatingChartId) || choosingChart);
  const editing = addOns.find((addOn) => addOn.id === editingId) || null;
  const initial = useMemo(() => (editing && { ...editing }) || copying, [editing, copying]);

  const close = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setCopying(null);
  };

  // A copy opens as a new add-on, as on a confirm: its own key, no image, no seat section.
  const copy = (addOn) => {
    const { id, clientKey } = blankAddOn();
    setCopying({
      ...addOn, id, clientKey, name: copyName(addOn.name, addOns.map((other) => other.name)), sectionKey: undefined,
      imageFile: null, imageUrl: null, imageFileName: null, removeImage: false,
    });
    setDrawerOpen(true);
  };

  const actionsFor = (addOn) => [
    { label: "Edit", icon: Edit01, onClick: () => { setEditingId(addOn.id); setDrawerOpen(true); } },
    ...(addOn.sectionKey ? [] : [{ label: "Copy", icon: Copy01, onClick: () => copy(addOn) }]),
    ...(addOn.sectionKey && addOnSeatingChartId
      ? []
      : [{ label: "Delete", icon: Trash01, destructive: true, onClick: () => onChange(addOns.filter((candidate) => candidate.id !== addOn.id)) }]),
  ];

  const save = (draft) => {
    onChange(
      editing
        ? addOns.map((addOn) => (addOn.id === editing.id ? { ...draft, id: addOn.id } : addOn))
        : [...addOns, { ...draft, id: blankAddOn().id }],
    );
    close();
  };

  return (
    <div className="series-tab-panel">
      <div className="series-ticket-toolbar">
        <p className="series-edit-hint">
          Add-ons let guests enhance their visit (parking, merchandise, extras). They're offered at checkout alongside
          tickets.
        </p>
        <div className="series-ticket-toolbar-actions">
          {addOnCharts.length > 0 && !chartShown && (
            <Button variant="secondary" onClick={() => setChoosingChart(true)}>
              Add Seating Chart
            </Button>
          )}
          <Button variant="secondary" icon={Plus} onClick={() => setDrawerOpen(true)}>
            Add Add-on
          </Button>
        </div>
      </div>

      {settings && onSettingsChange && (
        <AddOnsCopySection
          settings={settings}
          defaults={addOnsCopyDefaults}
          templates={messageTemplates}
          templatesUrl={messageTemplatesUrl}
          csrfToken={csrfToken}
          onChange={onSettingsChange}
        />
      )}

      {chartShown && (
        <div className="series-seated-admission" data-testid="add-on-chart-picker">
          <SeatingChartPicker
            charts={addOnCharts}
            value={addOnSeatingChartId}
            sectionsUrl={sectionsUrl}
            editChartUrl={editChartUrl}
            context={ADD_ON_CHART}
            showTypeLabel={showTypeLabel}
            title="Add-on Seating Chart"
            description="Each section becomes a seated add-on, like a table or a parking space."
            swapWarning="Choosing another chart resets this show type's seated add-ons, and its add-on seat holds and kills."
            onChooseVenue={onChooseVenue}
            onPick={(chartId, sections) => {
              setChoosingChart(false);
              onPickChart(chartId, sections);
            }}
          />
          <button
            type="button"
            className="series-notice-link series-chart-remove"
            onClick={() => {
              setChoosingChart(false);
              if (addOnSeatingChartId) onRemoveChart();
            }}
          >
            {addOnSeatingChartId ? "Remove add-on seating chart" : "Cancel"}
          </button>
        </div>
      )}

      {addOns.length === 0 ? (
        <p className="series-edit-hint">No add-ons yet.</p>
      ) : (
        <AddOnsTableFrame
          items={addOns}
          getId={(addOn) => addOn.id}
          isDragDisabled={(addOn) => Boolean(addOn.sectionKey)}
          onMove={(from, to) => onChange(moveRow(addOns, from, to))}
          renderRow={(addOn, index, provided, snapshot) => (
            <SeriesAddOnRow key={addOn.id} addOn={addOn} provided={provided} snapshot={snapshot} actions={actionsFor(addOn)} />
          )}
        />
      )}

      <AddOnDrawer
        open={drawerOpen}
        initial={initial}
        takenNames={addOns.filter((addOn) => addOn.id !== editingId).map((addOn) => addOn.name.trim())}
        defaultCheckInPeriod={defaultCheckInPeriod}
        feeQuoteUrl={feeQuoteUrl}
        venueOwnershipId={venueOwnershipId}
        csrfToken={csrfToken}
        ticketTypeNames={ticketTypeNames}
        showActivityNames={showActivityNames}
        manualTaxes={manualTaxes}
        canUseDynamicPricing={canUseDynamicPricing}
        dynamicPricingDefault={dynamicPricingDefault}
        timedEntry={timedEntry}
        onCancel={close}
        onSave={save}
      />
    </div>
  );
};

AddOnsTab.propTypes = {
  addOns: PropTypes.arrayOf(PropTypes.object).isRequired,
  ticketTypeNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  showActivityNames: PropTypes.arrayOf(PropTypes.string),
  manualTaxes: PropTypes.arrayOf(PropTypes.object),
  canUseDynamicPricing: PropTypes.bool,
  dynamicPricingDefault: PropTypes.bool,
  settings: PropTypes.object,
  addOnsCopyDefaults: PropTypes.object,
  messageTemplates: PropTypes.object,
  messageTemplatesUrl: PropTypes.string,
  onSettingsChange: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  addOnCharts: PropTypes.arrayOf(PropTypes.object),
  addOnSeatingChartId: PropTypes.number,
  sectionsUrl: PropTypes.string,
  editChartUrl: PropTypes.string,
  showTypeLabel: PropTypes.string,
  onPickChart: PropTypes.func,
  onRemoveChart: PropTypes.func,
  onChooseVenue: PropTypes.func,
  timedEntry: PropTypes.bool,
  defaultCheckInPeriod: PropTypes.string,
  feeQuoteUrl: PropTypes.string,
  venueOwnershipId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  csrfToken: PropTypes.string,
};

export default AddOnsTab;
